import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, AlertCircle, CheckCircle2, Image as ImageIcon, FileText, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Admin-only Vessel Data Audit dashboard.
 *
 * Hits `GET /api/admin/vessels-audit` and renders a triage list grouped by
 * severity so the operator can quickly see which flyers have garbage data
 * (empty key features, "Unknown" PP values, AI-hallucinated engine counts,
 * etc.) and click straight through to the Vessel editor to fix each one.
 */
const SEV_META = {
  high: {
    icon: AlertTriangle,
    iconCls: "text-[#EF4444]",
    badgeCls: "bg-[#FDECEC] text-[#EF4444] border-[#EF4444]/40",
    label: "High",
  },
  medium: {
    icon: AlertCircle,
    iconCls: "text-[#FBBF24]",
    badgeCls: "bg-[#FFF7E6] text-[#FBBF24] border-[#FBBF24]/40",
    label: "Medium",
  },
  low: {
    icon: AlertCircle,
    iconCls: "text-[#5B6B7C]",
    badgeCls: "bg-[#EDF1F5] text-[#5B6B7C] border-[#C4D0DB]",
    label: "Low",
  },
  ok: {
    icon: CheckCircle2,
    iconCls: "text-[#10B981]",
    badgeCls: "bg-[#E7F6EE] text-[#10B981] border-[#10B981]/40",
    label: "Clean",
  },
};

export default function VesselsAudit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("flagged"); // all | flagged | high | medium | low | ok
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [showVerified, setShowVerified] = useState({});
  const [busyKey, setBusyKey] = useState(null);

  // Mark an issue verified (or restore it). The API returns the vessel's
  // recomputed audit row; swap it into state and rebuild the summary.
  const toggleDismiss = async (vesselId, key, dismissed) => {
    setBusyKey(key);
    try {
      const { data: row } = await api.post(`/admin/vessels-audit/${vesselId}/dismiss`, {
        key,
        dismissed,
      });
      setData((d) => {
        const vessels = d.vessels.map((x) => (x.id === row.id ? row : x));
        const summary = { ...d.summary, high: 0, medium: 0, low: 0, ok: 0 };
        vessels.forEach((x) => {
          summary[x.severity] += 1;
        });
        return { ...d, vessels, summary };
      });
      toast.success(dismissed ? "Marked verified — it won't resurface unless the data changes" : "Issue restored");
    } catch {
      toast.error("Could not update — try again");
    } finally {
      setBusyKey(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/vessels-audit");
      setData(res.data);
      setRefreshedAt(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Auto-refresh when the admin tabs back to this page. The flyer + vessel
  // editor often open in a separate tab — coming back here should reflect
  // the fix without needing the manual "Re-run audit" button.
  useEffect(() => {
    const onFocus = () => {
      if (!document.hidden) load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === "all") return data.vessels;
    if (filter === "flagged") return data.vessels.filter((v) => v.severity !== "ok");
    return data.vessels.filter((v) => v.severity === filter);
  }, [data, filter]);

  if (loading || !data) {
    return <div className="p-10 text-[#5B6B7C]">Running audit…</div>;
  }

  const s = data.summary;

  return (
    <div className="p-6 sm:p-8 lg:p-10 max-w-[1400px] mx-auto" data-testid="vessels-audit-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#5B6B7C] font-display">
            Quality Assurance
          </div>
          <h1 className="font-display text-4xl font-bold text-[#011836] mt-1">Vessel Data Audit</h1>
          <p className="text-sm text-[#5B6B7C] mt-2 max-w-2xl">
            Every flyer renders raw database content with no validation. This page flags vessels
            whose data probably needs human review before clients see it — empty key features,
            placeholder values, AI-hallucinated engine counts, contradictions between description
            and Principal Particulars, and so on.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {refreshedAt && (
            <span
              className="text-[10px] uppercase tracking-[0.2em] text-[#5B6B7C] font-display"
              data-testid="audit-refreshed-at"
              title={refreshedAt.toLocaleString()}
            >
              Updated {refreshedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
          <Button
            onClick={load}
            variant="outline"
            className="border-[#C4D0DB] text-[#011836] hover:bg-[#EDF1F5]"
            data-testid="audit-refresh-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Re-run audit
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { key: "all", label: "All vessels", value: s.total, cls: "text-[#011836]" },
          { key: "high", label: "High severity", value: s.high, cls: SEV_META.high.iconCls },
          { key: "medium", label: "Medium", value: s.medium, cls: SEV_META.medium.iconCls },
          { key: "low", label: "Low", value: s.low, cls: SEV_META.low.iconCls },
          { key: "ok", label: "Clean", value: s.ok, cls: SEV_META.ok.iconCls },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setFilter(t.key)}
            className={`text-left p-4 rounded-lg border transition-colors ${
              filter === t.key
                ? "border-[#0088CC] bg-[#E6F2FA]"
                : "border-[#DDE5EC] bg-[#F6F7F8] hover:border-[#C4D0DB]"
            }`}
            data-testid={`audit-tile-${t.key}`}
          >
            <div className={`font-display text-3xl font-bold ${t.cls}`}>{t.value}</div>
            <div className="text-xs uppercase tracking-wider text-[#5B6B7C] mt-1">{t.label}</div>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setFilter("flagged")}
          className={`text-left p-4 rounded-lg border transition-colors col-span-2 md:col-span-1 ${
            filter === "flagged"
              ? "border-[#0088CC] bg-[#E6F2FA]"
              : "border-[#DDE5EC] bg-[#F6F7F8] hover:border-[#C4D0DB]"
          }`}
          data-testid="audit-tile-flagged"
        >
          <div className="font-display text-3xl font-bold text-[#EF4444]">{s.high + s.medium + s.low}</div>
          <div className="text-xs uppercase tracking-wider text-[#5B6B7C] mt-1">Needs review</div>
        </button>
      </div>

      {/* Vessel rows */}
      {filtered.length === 0 ? (
        <div className="text-center text-sm text-[#5B6B7C] py-16">
          {filter === "ok"
            ? "No vessels in the 'clean' bucket yet — keep fixing!"
            : "No vessels match this filter. 🎉"}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((v) => {
            const sev = SEV_META[v.severity];
            const SevIcon = sev.icon;
            return (
              <div
                key={v.id}
                className="rounded-lg border border-[#DDE5EC] bg-[#F6F7F8] p-4"
                data-testid={`audit-row-${v.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <SevIcon className={`h-5 w-5 shrink-0 ${sev.iconCls}`} />
                    <div className="min-w-0">
                      <div className="font-display font-semibold text-[#011836] truncate flex items-center gap-2">
                        {v.name}
                        <span
                          className={`text-[10px] uppercase tracking-wider font-display font-semibold px-2 py-0.5 rounded border ${sev.badgeCls}`}
                        >
                          {sev.label}
                        </span>
                        {!v.is_live && (
                          <span className="text-[10px] uppercase tracking-wider text-[#5B6B7C] border border-[#C4D0DB] px-2 py-0.5 rounded">
                            Draft
                          </span>
                        )}
                      </div>
                      <div className="text-xs uppercase tracking-wider text-[#5B6B7C] mt-0.5 truncate">
                        {[v.vessel_type, v.sub_category, v.material].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-[#5B6B7C] shrink-0">
                    <span className="inline-flex items-center gap-1" title="Has render image">
                      <ImageIcon className={`h-3.5 w-3.5 ${v.has_render ? "text-[#0088CC]" : "text-[#B6C2CD]"}`} />
                      {v.has_render ? "Render" : "No render"}
                    </span>
                    <span className="inline-flex items-center gap-1" title="Has GA PDF">
                      <FileText className={`h-3.5 w-3.5 ${v.has_ga_pdf ? "text-[#0088CC]" : "text-[#B6C2CD]"}`} />
                      {v.has_ga_pdf ? "GA" : "No GA"}
                    </span>
                    <span>PP {v.pp_count}</span>
                    <span>
                      KF {v.kf_count_non_empty}/{v.kf_count_total}
                    </span>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link
                      to={`/vessels/${v.id}/flyer`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-[#C4D0DB] text-xs text-[#011836] hover:bg-[#EDF1F5] font-display tracking-wide"
                      data-testid={`audit-view-flyer-${v.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View flyer <ExternalLink className="h-3 w-3" />
                    </Link>
                    <Link
                      to={`/vessels?focus=${v.id}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#0088CC] hover:bg-[#006DA6] text-xs text-white font-display tracking-wide"
                      data-testid={`audit-edit-${v.id}`}
                    >
                      Open editor
                    </Link>
                  </div>
                </div>

                {v.issues.filter((i) => !i.dismissed).length > 0 && (
                  <ul className="mt-3 space-y-1.5 pl-8">
                    {v.issues.filter((i) => !i.dismissed).map((issue) => (
                      <li key={issue.key} className="text-sm text-[#33475C] flex gap-2 items-start">
                        <span
                          className={`text-[10px] uppercase tracking-wider font-display font-semibold px-1.5 py-0.5 rounded border shrink-0 mt-0.5 ${SEV_META[issue.severity].badgeCls}`}
                        >
                          {issue.severity}
                        </span>
                        <span className="flex-1 min-w-0 break-words">{issue.msg}</span>
                        <button
                          type="button"
                          onClick={() => toggleDismiss(v.id, issue.key, true)}
                          disabled={busyKey === issue.key}
                          className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded border border-[#10B981]/40 text-[10px] uppercase tracking-wider font-display font-semibold text-[#10B981] hover:bg-[#E7F6EE] transition-colors"
                          title="Checked against the GA / source docs — hide this issue until the data changes"
                          data-testid={`audit-verify-${v.id}-${issue.key}`}
                        >
                          <CheckCircle2 className="h-3 w-3" /> Mark verified
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {v.issues.some((i) => i.dismissed) && (
                  <div className="mt-3 pl-8">
                    <button
                      type="button"
                      onClick={() =>
                        setShowVerified((s) => ({ ...s, [v.id]: !s[v.id] }))
                      }
                      className="text-[11px] uppercase tracking-wider font-display font-semibold text-[#10B981] hover:underline inline-flex items-center gap-1"
                      data-testid={`audit-verified-toggle-${v.id}`}
                    >
                      <CheckCircle2 className="h-3 w-3" />
                      {v.issues.filter((i) => i.dismissed).length} verified
                      {showVerified[v.id] ? " — hide" : " — show"}
                    </button>
                    {showVerified[v.id] && (
                      <ul className="mt-2 space-y-1.5">
                        {v.issues.filter((i) => i.dismissed).map((issue) => (
                          <li
                            key={issue.key}
                            className="text-sm text-[#5B6B7C]/70 flex gap-2 items-start"
                            data-testid={`audit-verified-row-${v.id}-${issue.key}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 text-[#10B981]/60 shrink-0 mt-0.5" />
                            <span className="flex-1 min-w-0 break-words line-through decoration-[#94A3B8]/40">
                              {issue.msg}
                            </span>
                            <button
                              type="button"
                              onClick={() => toggleDismiss(v.id, issue.key, false)}
                              disabled={busyKey === issue.key}
                              className="shrink-0 text-[10px] uppercase tracking-wider font-display font-semibold text-[#5B6B7C] hover:text-[#011836] border border-[#C4D0DB] rounded px-2 py-0.5"
                              data-testid={`audit-restore-${v.id}-${issue.key}`}
                            >
                              Restore
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
